<template>
  <div
    class="absolute left-3 top-3 flex gap-0.5 rounded-lg bg-bgr shadow-lg"
    role="menubar"
  >
    <div class="relative">
      <button
        class="flex h-10 min-w-[35px] cursor-pointer items-center justify-center border-none bg-bgr px-4 text-left text-lg text-txt hover:text-thm"
        :class="{
          'text-thm font-medium': MAP_ROADMAP_DROPDOWN_TYPES.includes(mapType),
          'rounded-l-lg': !showMapMenu && !showSatelliteMenu,
          'rounded-tl-lg': showMapMenu,
          'rounded-tl-lg rounded-bl-lg': showSatelliteMenu,
        }"
        @click="toggleMapMenu"
        @blur="closeMapMenu"
        type="button"
        role="menuitemradio"
        :aria-checked="mapType === 'roadmap'"
        aria-haspopup="true"
      >
        {{ t(`${trPrefixMap}roadmap`) }}
      </button>

      <ul
        v-show="showMapMenu"
        class="absolute left-0 top-10 z-10 min-w-full block rounded-b-lg bg-bgr p-0.5 shadow-lg"
        role="menu"
      >
        <li
          class="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left text-lg text-txt transition-colors hover:bg-gray-100"
          @click="handleMapTypeChange('terrain')"
          @mousedown.prevent
          tabindex="-1"
          role="menuitemcheckbox"
          :aria-checked="mapType === 'terrain'"
        >
          <RedesignWebccCheckbox
            :value="'terrain'"
            :model-value="mapType === 'terrain'"
            size="sm"
            @click.stop="handleMapTypeChange('terrain')"
          />
          <span class="text-base">{{ t(`${trPrefixMap}terrain`) }}</span>
        </li>
      </ul>
    </div>

    <div class="relative border-l">
      <button
        class="flex h-10 min-w-[64px] cursor-pointer items-center justify-center border-none bg-bgr px-4 text-left text-lg text-txt hover:text-thm"
        :class="{
          'text-thm font-medium':
            MAP_SATELLITE_DROPDOWN_TYPES.includes(mapType),
          'rounded-r-lg': !showMapMenu && !showSatelliteMenu,
          'rounded-tr-lg rounded-br-lg': showMapMenu,
          'rounded-tr-lg': showSatelliteMenu,
        }"
        @click="toggleSatelliteMenu"
        @blur="closeSatelliteMenu"
        type="button"
        role="menuitemradio"
        :aria-checked="MAP_SATELLITE_DROPDOWN_TYPES.includes(mapType)"
        aria-haspopup="true"
      >
        {{ t(`${trPrefixMap}satellite`) }}
      </button>

      <ul
        v-show="showSatelliteMenu"
        class="absolute right-0 top-10 z-10 min-w-full block rounded-b-lg bg-bgr p-0.5 shadow-lg"
        role="menu"
      >
        <li
          class="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left text-lg text-txt transition-colors hover:bg-gray-100"
          @click="handleMapTypeChange('hybrid')"
          @mousedown.prevent
          tabindex="-1"
          role="menuitemcheckbox"
          :aria-checked="mapType === 'hybrid'"
        >
          <RedesignWebccCheckbox
            :value="'hybrid'"
            :model-value="mapType === 'hybrid'"
            size="sm"
            @click.stop="handleMapTypeChange('hybrid')"
          />
          <span class="text-base">{{ t(`${trPrefixMap}hybrid`) }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  MAP_ROADMAP_DROPDOWN_TYPES,
  MAP_SATELLITE_DROPDOWN_TYPES,
  MAP_TYPE_DEFAULT,
  mapTypeToGoogleMapTypeId,
  mapGoogleMapTypeIdToMapType,
} from '~/utils/map'

const trPrefixMap = 'www.map.type.'

const { t } = useI18n()

const props = defineProps<{ map: google.maps.Map }>()

const emit = defineEmits<{
  mapTypeChange: [mapType: MapType]
}>()

const mapType = ref<MapType>(MAP_TYPE_DEFAULT)
const showMapMenu = ref(false)
const showSatelliteMenu = ref(false)

function updateMapType(newMapType: MapType) {
  if (!props.map || typeof google === 'undefined') return

  const googleMapType = mapTypeToGoogleMapTypeId()[newMapType]
  if (!googleMapType) return

  props.map.setMapTypeId(googleMapType)
  mapType.value = newMapType
  emit('mapTypeChange', newMapType)
}

function handleMapTypeChange(type: MapType) {
  if (!props.map) return

  updateMapType(getTargetType(type, mapType.value))

  closeMapMenu()
  closeSatelliteMenu()
}

function toggleMapMenu() {
  if (!props.map) return

  showMapMenu.value = !showMapMenu.value
  closeSatelliteMenu()

  if (!MAP_ROADMAP_DROPDOWN_TYPES.includes(mapType.value)) {
    updateMapType('roadmap')
  }
}

function toggleSatelliteMenu() {
  if (!props.map) return

  showSatelliteMenu.value = !showSatelliteMenu.value
  closeMapMenu()

  if (!MAP_SATELLITE_DROPDOWN_TYPES.includes(mapType.value)) {
    updateMapType('hybrid')
  }
}

function closeMapMenu() {
  showMapMenu.value = false
}

function closeSatelliteMenu() {
  showSatelliteMenu.value = false
}

watch(
  () => props.map,
  (newMap) => {
    if (typeof google === 'undefined') return

    mapType.value = newMap?.getMapTypeId()
      ? mapGoogleMapTypeIdToMapType()[newMap.getMapTypeId()!]
      : MAP_TYPE_DEFAULT
  },
  { immediate: true },
)
</script>
